import { useState } from 'react';
import { Donut } from '../../../../../campaign-detail/components/insights/charts/Donut';
import { SC } from '../../constants';
import type { AnalysisResult } from '../../types';

const SENT_ROWS = [
  { key: 'avg_positive_pct', label: 'Positive', color: SC.positive },
  { key: 'avg_neutral_pct', label: 'Neutral', color: SC.neutral },
  { key: 'avg_negative_pct', label: 'Negative', color: SC.negative },
  { key: 'avg_toxic_pct', label: 'Toxic', color: SC.toxic },
] as const;

/** Figma "Channel Average" donut card (node 1741:55609). Donut + center
 *  "{dominant}% {label}" + per-sentiment legend. Hover a ring segment or a
 *  legend row to dim the rest and swap the center label to that segment —
 *  matches the interactive-donut pattern used by Content Types / Followers
 *  Quality / Audience Fit elsewhere in the app. */
export function ChannelAverageDonut({
  summary,
}: {
  summary: AnalysisResult['summary'];
}) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const rows = SENT_ROWS.map((r) => ({
    label: r.label,
    color: r.color,
    val: summary[r.key as keyof AnalysisResult['summary']] as number | null,
  })).filter((r) => r.val != null) as { label: string; color: string; val: number }[];

  // Default center = Positive (not whichever category happens to be largest).
  const defaultRow = rows.find((r) => r.label === 'Positive') ?? rows[0] ?? null;
  const hovered = hoveredIdx != null ? rows[hoveredIdx] : null;
  const segments = rows.map((r) => ({ pct: r.val, color: r.color }));
  const center = hovered ?? defaultRow;

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 500, color: '#171717' }}>
        CHANNEL AVERAGE
      </div>
      {/* Figma: donut centred on top, 2×2 legend below */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 24 }}>
        <div style={{ position: 'relative', width: 250, height: 250, flexShrink: 0 }}>
          <Donut segments={segments} size={250} thickness={29} activeIndex={hoveredIdx} onHoverChange={setHoveredIdx} />
          {center && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <span style={{ fontSize: 36, fontWeight: 500, color: '#171717', lineHeight: 1 }}>
                {Math.round(center.val)}%
              </span>
              <span style={{ fontSize: 14, fontWeight: 500, color: '#737373', letterSpacing: '0.04em', marginTop: 4 }}>
                {center.label}
              </span>
            </div>
          )}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            columnGap: 32,
            rowGap: 12,
            width: '100%',
            maxWidth: 300,
          }}
        >
          {rows.map((r, i) => (
            <div
              key={r.label}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                borderRadius: 6,
                padding: '2px 4px',
                margin: '-2px -4px',
                background: hoveredIdx === i ? 'var(--muted)' : 'transparent',
                transition: 'background-color 0.15s ease',
                cursor: 'pointer',
              }}
            >
              <span style={{ width: 12, height: 12, borderRadius: 4, background: r.color, flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 16, fontWeight: 500, color: '#404040' }}>{r.label}</span>
              <span style={{ fontSize: 16, fontWeight: 600, color: '#171717' }}>{r.val.toFixed(1)}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
